import { useMemo } from "react";
import type { Control, FieldPath, FieldValues } from "react-hook-form";
import { SelectSearch, SelectSearchBase } from "@telluscompany/design-system";

import { useAdminStructuresQuery } from "../../features/structures/api/structures";

interface BaseProps {
  /** Complexo cujas estruturas serão listadas. Quando vazio, o combobox fica desabilitado. */
  complexId: string | null | undefined;
  label: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  clearable?: boolean;
  notFoundText?: string;
  /** Limite de opções carregadas. Default 100. */
  limit?: number;
}

interface ControlledProps<TFieldValues extends FieldValues> extends BaseProps {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
  value?: never;
  onChange?: never;
}

interface UncontrolledProps extends BaseProps {
  control?: never;
  name?: never;
  value: string | "" | undefined;
  onChange: (value: string) => void;
}

type StructureComboboxProps<TFieldValues extends FieldValues = FieldValues> =
  | ControlledProps<TFieldValues>
  | UncontrolledProps;

/**
 * Combobox compartilhado para selecionar uma estrutura administrativa,
 * sempre dependente de um `complexId`. Reaproveita `useAdminStructuresQuery`
 * (que já trata `complexId === null` retornando enabled=false).
 *
 * Em modo uncontrolled (`value`/`onChange`), renderiza `SelectSearchBase`
 * diretamente — sem `useForm` interno — para que o estado externo do
 * consumidor seja a única fonte de verdade. Isso evita o efeito de
 * "ping-pong" entre o form interno antigo e o reset feito pelo pai quando
 * o `complexId` muda, que provocava loop de requisições.
 */
export function StructureCombobox<TFieldValues extends FieldValues = FieldValues>(
  props: StructureComboboxProps<TFieldValues>,
) {
  const {
    complexId,
    label,
    placeholder,
    disabled,
    className,
    clearable,
    notFoundText,
    limit = 100,
  } = props;

  const { data, isLoading } = useAdminStructuresQuery(complexId ?? null, {
    page: 1,
    limit,
  });

  const options = useMemo(
    () =>
      data?.data?.map((structure) => ({
        label: structure.name,
        value: structure.id,
      })) ?? [],
    [data],
  );

  const isDisabled = disabled || isLoading || !complexId;

  if ("control" in props && props.control && props.name) {
    return (
      <SelectSearch
        control={props.control}
        name={props.name}
        label={label}
        placeholder={placeholder}
        options={options}
        disabled={isDisabled}
        className={className}
        clearable={clearable}
        notFoundText={notFoundText}
      />
    );
  }

  const { value, onChange } = props as UncontrolledProps;

  return (
    <div className={`grid gap-1 ${className ?? ""}`.trim()}>
      <label className={isDisabled ? "text-text-disabled" : undefined}>
        {label}
      </label>
      <SelectSearchBase
        value={value ?? ""}
        onChange={(next) => onChange(typeof next === "string" ? next : "")}
        placeholder={placeholder}
        options={options}
        disabled={isDisabled}
        clearable={clearable}
        notFoundText={notFoundText}
      />
    </div>
  );
}
